import React, { useState } from 'react';
import logoMini from 'assets/img/brand/logo_mini.png';
import NewDesign from 'components/NewDesign';
import { Separator } from 'shadcn/components/separator';
import TabTypography from './Tabs/TabComponents/TabTypography';
import TabButton from './Tabs/TabComponents/TabButton';
import ThemeSwitcher from 'components/ThemeSwitcher';
import TabTooltip from './Tabs/TabComponents/TabTooltip';
import TabStyle from './Tabs/TabComponents/TabStyle';
import TabBadge from './Tabs/TabComponents/TabBadge';
import TabForm from './Tabs/TabComponents/TabForm';
import TabDialog from './Tabs/TabComponents/TabDialog';
import TabSeparator from './Tabs/TabComponents/TabSeparator';
import TabInput from './Tabs/TabFormikFields/TabInput';
import TabSelect from './Tabs/TabFormikFields/TabSelect';
import TabCheckbox from './Tabs/TabFormikFields/TabCheckbox';
import { useQueryParam } from 'hooks/useQueryParam';
import { useHistory, useLocation } from 'react-router';
import TabRadio from './Tabs/TabFormikFields/TabRadio';
import TabDateRange from './Tabs/TabFormikFields/TabDateRange';
import TabDateTime from './Tabs/TabFormikFields/TabDateTime';
import TabAsyncSelect from './Tabs/TabFormikFields/TabAsyncSelect';
import TabDialogGuide from './Tabs/TabGuides/TabDialogGuide';
import TabFullPageWithTableGuide from './Tabs/TabGuides/TabFullPageWithTableGuide';
import TabFullPageWithTableInfiniteScrollGuide from './Tabs/TabGuides/TabFullPageWithTableInfiniteScrollGuide';
import TabDataGrid from './Tabs/TabComponents/TabDataGrid';

const DocumentPage = (props) => {
  //! State
  const history = useHistory();
  const queryParams = useQueryParam();
  const location = useLocation();
  const [tab, setTab] = useState(queryParams?.tab || 'quick-start-dialog');

  const componentTabs = [
    {
      label: 'Colors',
      value: 'colors',
    },
    {
      label: 'Typography',
      value: 'typography',
    },
    {
      label: 'Button',
      value: 'button',
    },
    {
      label: 'Tooltip',
      value: 'tooltip',
    },
    {
      label: 'Badge',
      value: 'badge',
    },
    {
      label: 'Form',
      value: 'form',
    },
    {
      label: 'Dialog',
      value: 'dialog',
    },
    {
      label: 'DataGrid / Table',
      value: 'data-grid',
    },
    {
      label: 'Separator',
      value: 'separator',
    },
  ];

  const formikFieldTabs = [
    {
      label: 'Input',
      value: 'formik-fields-input',
    },
    {
      label: 'Select',
      value: 'formik-fields-select',
    },
    {
      label: 'Async Select',
      value: 'formik-fields-async-select',
    },
    {
      label: 'Checkbox',
      value: 'formik-fields-checkbox',
    },
    {
      label: 'Radio',
      value: 'formik-fields-radio',
    },
    {
      label: 'Date Range',
      value: 'formik-fields-date-range',
    },
    {
      label: 'Date Time',
      value: 'formik-fields-date-time',
    },
  ];

  const guidesTab = [
    { label: 'Example new Dialog', value: 'quick-start-dialog' },
    { label: 'Example with Table + Query', value: 'quick-start-table-query' },
    { label: 'Example with Table + Infinite Scroll', value: 'quick-start-table-scroll' },
  ];

  //! Function
  const onClickTab = (tab) => {
    setTab(tab);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('tab', tab);
    history.push(`${location.pathname}?${searchParams.toString()}`);
  };

  //! Render
  const mappingTab = {
    // Components
    typography: <TabTypography />,
    button: <TabButton />,
    tooltip: <TabTooltip />,
    colors: <TabStyle />,
    badge: <TabBadge />,
    form: <TabForm />,
    dialog: <TabDialog />,
    separator: <TabSeparator />,
    'data-grid': <TabDataGrid />,
    // Formik Fields
    'formik-fields-input': <TabInput />,
    'formik-fields-select': <TabSelect />,
    'formik-fields-async-select': <TabAsyncSelect />,
    'formik-fields-checkbox': <TabCheckbox />,
    'formik-fields-radio': <TabRadio />,
    'formik-fields-date-range': <TabDateRange />,
    'formik-fields-date-time': <TabDateTime />,
    'selection-components': <TabTypography />,
    // Guides
    'quick-start-dialog': <TabDialogGuide />,
    'quick-start-table-query': <TabFullPageWithTableGuide />,
    'quick-start-table-scroll': <TabFullPageWithTableInfiniteScrollGuide />,
  };

  return (
    <div className='component:DocumentPage tw-bg-primary-dark tw-min-h-[100vh]'>
      <div className='tw-flex tw-w-full tw-justify-center tw-pt-2 tw-items-center tw-relative'>
        <img alt='logo-nav' className='logo tw-w-10' src={logoMini} />
        <NewDesign.Typography className='tw-text-xl tw-ml-2'>Documentation</NewDesign.Typography>

        <div className='tw-absolute tw-right-10 tw-top-[50%] tw-translate-y-[-50%]'>
          <ThemeSwitcher />
        </div>
      </div>
      <Separator className='tw-my-5' />

      <div className='tw-p-5'>
        <div className='document-content tw-grid tw-grid-cols-[max-content_1fr] tw-gap-6'>
          <div className='docs__side-bar tw-flex tw-flex-col tw-gap-4 tw-h-[max-content] tw-sticky tw-top-2'>
            <div className='tw-flex tw-flex-col tw-gap-1 tw-relative'>
              <NewDesign.Typography className='tw-text-sm tw-font-semibold tw-text-gray-500 tw-mb-2'>
                Guides
              </NewDesign.Typography>

              {guidesTab.map((el) => {
                return (
                  <NewDesign.Button
                    key={el.value}
                    onClick={() => {
                      onClickTab(el.value);
                    }}
                    variant={el.value === tab ? 'default' : 'ghost'}
                    className='tw-text-md tw-justify-start'
                  >
                    {el.label}
                  </NewDesign.Button>
                );
              })}
            </div>

            <div className='tw-flex tw-flex-col tw-gap-1 tw-relative'>
              <NewDesign.Typography className='tw-text-sm tw-font-semibold tw-text-gray-500 tw-mb-2'>
                Components
              </NewDesign.Typography>

              {componentTabs.map((el) => {
                return (
                  <NewDesign.Button
                    key={el.value}
                    onClick={() => {
                      onClickTab(el.value);
                    }}
                    variant={el.value === tab ? 'default' : 'ghost'}
                    className='tw-text-md tw-justify-start'
                  >
                    {el.label}
                  </NewDesign.Button>
                );
              })}
            </div>

            <div className='tw-flex tw-flex-col tw-gap-1 tw-relative'>
              <NewDesign.Typography className='tw-text-sm tw-font-semibold tw-text-gray-500 tw-mb-2'>
                Formik Fields
              </NewDesign.Typography>

              {formikFieldTabs.map((el) => {
                return (
                  <NewDesign.Button
                    key={el.value}
                    onClick={() => {
                      onClickTab(el.value);
                    }}
                    variant={el.value === tab ? 'default' : 'ghost'}
                    className='tw-text-md tw-justify-start'
                  >
                    {el.label}
                  </NewDesign.Button>
                );
              })}
            </div>
          </div>

          <div className='docs__content default-border tw-p-4 tw-mx-auto tw-w-full tw-max-w-[1366px]'>
            {mappingTab?.[tab] || null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentPage;
